/**
 * Composable for binding keyboard shortcuts to player actions.
 *
 * Receives action callbacks and state refs instead of reading from stores.
 * Automatically binds on mount and unbinds on unmount.
 *
 * @param options - Object with action callbacks and state refs
 * @returns Shortcut definitions plus enable/disable functions
 *
 * @example
 * useKeyboardShortcuts({
 *   containerRef: playerContainer,
 *   togglePlay: () => player.togglePlay(),
 *   seek: (t) => player.seek(t),
 *   currentTime: player.currentTime,
 *   duration: player.duration,
 *   volume: player.volume,
 * })
 */

import { onMounted, onUnmounted, ref, watch, type Ref, type ComputedRef } from 'vue'

export interface KeyboardShortcut {
  key: string | string[]
  handler: () => void
  description: string
  preventDefault?: boolean
  /** It moves the playhead, so it is the viewer's only while `canSeek` says so. */
  seeks?: boolean
}

export interface KeyboardShortcutOptions {
  containerRef: Ref<HTMLElement | null>

  togglePlay: () => void
  seek: (time: number) => void
  /**
   * Whether the viewer may move the playhead (`seeking`). While it is `false` the keys that would —
   * the arrows, J and L, Home and End, the digits — are left to the page, as keys the player does
   * not take; `true` when absent.
   */
  canSeek?: Ref<boolean> | ComputedRef<boolean>
  setVolume: (volume: number) => void
  toggleMute: () => void
  toggleFullscreen: () => void
  toggleSubtitles?: () => void
  setPlaybackSpeed?: (speed: number) => void
  next?: () => void
  prev?: () => void
  togglePanel?: (panel: string) => void
  closePanel?: () => void
  exitFullscreen?: () => void

  onAction?: (type: string, text?: string) => void
  skipAd?: () => void
  isAdSkippable?: Ref<boolean> | ComputedRef<boolean>

  isPlaying: Ref<boolean> | ComputedRef<boolean>
  volume: Ref<number>
  currentTime: Ref<number>
  duration: Ref<number>
  playbackSpeed?: Ref<number>
  isFullscreen?: Ref<boolean> | ComputedRef<boolean>
  isPanelOpen?: Ref<boolean> | ComputedRef<boolean>
  hasSubtitles?: Ref<boolean> | ComputedRef<boolean>
  hasNextVideo?: Ref<boolean> | ComputedRef<boolean>
  hasPreviousVideo?: Ref<boolean> | ComputedRef<boolean>
  disabled?: Ref<boolean> | ComputedRef<boolean>
}

export function useKeyboardShortcuts(options: KeyboardShortcutOptions) {
  const {
    containerRef,
    togglePlay,
    seek,
    setVolume,
    toggleMute,
    toggleFullscreen,
    toggleSubtitles,
    setPlaybackSpeed,
    next,
    prev,
    togglePanel,
    closePanel,
    exitFullscreen,
    onAction,
    isPlaying,
    volume,
    currentTime,
    duration,
    playbackSpeed,
    isFullscreen,
    isPanelOpen,
    hasSubtitles,
    hasNextVideo,
    hasPreviousVideo,
  } = options

  const isActivated = ref(false)
  const speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

  const shortcuts: KeyboardShortcut[] = [
    {
      key: [' ', 'k'],
      handler: () => togglePlay(),
      description: 'Play/Pause',
      preventDefault: true,
    },
    {
      key: 'arrowleft',
      handler: () => {
        seek(Math.max(0, currentTime.value - 10))
        onAction?.('backward')
      },
      description: 'Rewind 10 seconds',
      preventDefault: true,
      seeks: true,
    },
    {
      key: 'arrowright',
      handler: () => {
        seek(Math.min(duration.value, currentTime.value + 10))
        onAction?.('forward')
      },
      description: 'Forward 10 seconds',
      preventDefault: true,
      seeks: true,
    },
    {
      key: 'j',
      handler: () => {
        seek(Math.max(0, currentTime.value - 10))
        onAction?.('backward')
      },
      description: 'Rewind 10 seconds',
      preventDefault: true,
      seeks: true,
    },
    {
      key: 'l',
      handler: () => {
        seek(Math.min(duration.value, currentTime.value + 10))
        onAction?.('forward')
      },
      description: 'Forward 10 seconds',
      preventDefault: true,
      seeks: true,
    },
    {
      key: 'home',
      handler: () => seek(0),
      description: 'Go to beginning',
      preventDefault: true,
      seeks: true,
    },
    {
      key: 'end',
      handler: () => seek(duration.value),
      description: 'Go to end',
      preventDefault: true,
      seeks: true,
    },
    {
      key: 'arrowup',
      handler: () => {
        setVolume(Math.min(1, volume.value + 0.1))
        onAction?.('volumeUp')
      },
      description: 'Increase volume',
      preventDefault: true,
    },
    {
      key: 'arrowdown',
      handler: () => {
        setVolume(Math.max(0, volume.value - 0.1))
        onAction?.('volumeDown')
      },
      description: 'Decrease volume',
      preventDefault: true,
    },
    {
      key: 'm',
      handler: () => toggleMute(),
      description: 'Mute/Unmute',
      preventDefault: true,
    },
    {
      key: 'f',
      handler: () => toggleFullscreen(),
      description: 'Toggle fullscreen',
      preventDefault: true,
    },
    {
      key: 'c',
      handler: () => {
        if (hasSubtitles?.value && toggleSubtitles) {
          toggleSubtitles()
        }
      },
      description: 'Toggle subtitles',
      preventDefault: true,
    },
    {
      key: '<',
      handler: () => {
        if (!setPlaybackSpeed || !playbackSpeed) return
        const currentIndex = speeds.indexOf(playbackSpeed.value)
        if (currentIndex > 0) {
          const newSpeed = speeds[currentIndex - 1]
          setPlaybackSpeed(newSpeed)
          onAction?.('speed', `${newSpeed}x`)
        }
      },
      description: 'Decrease playback speed',
      preventDefault: true,
    },
    {
      key: '>',
      handler: () => {
        if (!setPlaybackSpeed || !playbackSpeed) return
        const currentIndex = speeds.indexOf(playbackSpeed.value)
        if (currentIndex < speeds.length - 1) {
          const newSpeed = speeds[currentIndex + 1]
          setPlaybackSpeed(newSpeed)
          onAction?.('speed', `${newSpeed}x`)
        }
      },
      description: 'Increase playback speed',
      preventDefault: true,
    },
    {
      key: 'n',
      handler: () => {
        if (hasNextVideo?.value && next) {
          next()
        }
      },
      description: 'Next video',
      preventDefault: true,
    },
    {
      key: 'p',
      handler: () => {
        if (hasPreviousVideo?.value && prev) {
          prev()
        }
      },
      description: 'Previous video',
      preventDefault: true,
    },
    // Number keys for seeking to percentage positions
    ...Array.from({ length: 10 }, (_, i) => ({
      key: String(i),
      handler: () => seek((duration.value * i) / 10),
      description: `Go to ${i * 10}%`,
      preventDefault: true,
      seeks: true,
    })),
    {
      key: 'escape',
      handler: () => {
        if (isFullscreen?.value && exitFullscreen) {
          exitFullscreen()
        } else if (isPanelOpen?.value && closePanel) {
          closePanel()
        }
      },
      description: 'Exit fullscreen/Close panel',
      preventDefault: true,
    },
  ]

  const ARROW_KEYS = new Set(['arrowup', 'arrowdown', 'arrowleft', 'arrowright'])

  const handleKeydown = (event: KeyboardEvent) => {
    if (!isActivated.value) return

    // During ads, only allow skipping
    if (options.disabled?.value) {
      const key = event.key.toLowerCase()
      if ((key === ' ' || key === 'escape') && options.isAdSkippable?.value && options.skipAd) {
        event.preventDefault()
        options.skipAd()
      }
      return
    }

    const target = event.target as HTMLElement

    // Inside text inputs: block everything
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target.isContentEditable
    ) {
      return
    }

    // On interactive elements: only block Space and Enter (to avoid stealing native behavior)
    const role = target.getAttribute('role')
    const isSlider = role === 'slider' || (target instanceof HTMLInputElement && target.type === 'range')

    if (
      target instanceof HTMLButtonElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLAnchorElement ||
      role === 'button' ||
      role === 'menuitem' ||
      role === 'option' ||
      role === 'checkbox' ||
      role === 'radio' ||
      isSlider
    ) {
      const key = event.key.toLowerCase()
      // On sliders, also block arrow keys to avoid double-seek
      if (key === ' ' || key === 'enter' || (isSlider && ARROW_KEYS.has(key))) {
        return
      }
    }

    const key = event.key.toLowerCase()

    for (const shortcut of shortcuts) {
      const keys = Array.isArray(shortcut.key) ? shortcut.key : [shortcut.key]
      if (keys.includes(key)) {
        if (shortcut.seeks && options.canSeek?.value === false) return
        if (shortcut.preventDefault !== false) {
          event.preventDefault()
        }
        shortcut.handler()
        break
      }
    }
  }

  function handleActivation() {
    isActivated.value = true
  }

  function bindListeners(el: HTMLElement) {
    el.addEventListener('keydown', handleKeydown)
    el.addEventListener('click', handleActivation)
    el.addEventListener('focusin', handleActivation)
  }

  function unbindListeners(el: HTMLElement) {
    el.removeEventListener('keydown', handleKeydown)
    el.removeEventListener('click', handleActivation)
    el.removeEventListener('focusin', handleActivation)
  }

  let currentEl: HTMLElement | null = null

  const enableShortcuts = () => {
    const el = containerRef.value
    if (!el) return
    currentEl = el
    bindListeners(el)
  }

  const disableShortcuts = () => {
    if (currentEl) {
      unbindListeners(currentEl)
      currentEl = null
    }
  }

  watch(containerRef, (newEl, oldEl) => {
    if (oldEl) unbindListeners(oldEl)
    if (newEl) {
      currentEl = newEl
      bindListeners(newEl)
    }
  })

  onMounted(enableShortcuts)
  onUnmounted(disableShortcuts)

  return {
    shortcuts,
    isActivated,
    enableShortcuts,
    disableShortcuts,
  }
}
